import axios from "axios";
import {reactive} from "vue";

// Server-driven map tile + geocoding endpoints.
//
// Every tile and geocoding URL comes from the API (sourced from its .env),
// fetched from the public GET /auth/map-config at startup and kept in
// localStorage so maps render from the last-known config on the next load.
// Browsers do not allow setting a custom User-Agent, so no identity header is
// sent here; the web's identifier to the hosts is the Referer/Origin (domain),
// which only the server-side proxy (a later phase) can remove.
//
// The BOOTSTRAP values are the historical endpoints, used until the first
// server block arrives (or if it is unavailable), so this is a no-op until the
// server actually repoints an endpoint. They are not the source of truth.
const BOOTSTRAP = {
    streetUrl: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    satelliteUrl: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    reverseUrl: 'https://nominatim.openstreetmap.org/reverse',
    searchUrl: 'https://nominatim.openstreetmap.org/search',
};

const STORAGE_KEY = 'map_config_json';

export const mapConfig = reactive({...BOOTSTRAP});

const applyBlock = (block) => {
    if (!block || typeof block !== 'object') return;
    // enabled:false is the server kill-switch - keep last-known/bootstrap.
    if (block.enabled === false) return;
    const tiles = block.tiles || {};
    const street = tiles.street || {};
    const satellite = tiles.satellite || {};
    const geocode = block.geocode || {};
    if (typeof street.url === 'string' && street.url) mapConfig.streetUrl = street.url;
    if (typeof satellite.url === 'string' && satellite.url) mapConfig.satelliteUrl = satellite.url;
    if (typeof geocode.reverse_url === 'string' && geocode.reverse_url) mapConfig.reverseUrl = geocode.reverse_url;
    if (typeof geocode.search_url === 'string' && geocode.search_url) mapConfig.searchUrl = geocode.search_url;
};

// Load the persisted block immediately so the first map uses the last-known
// config even before the network call returns.
try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) applyBlock(JSON.parse(raw));
} catch { /* ignore */ }

let started = false;

/// Fetch the latest block from the public endpoint once at startup. Safe to
/// call unauthenticated (works on the public share page); never throws.
export const initMapConfig = async () => {
    if (started) return;
    started = true;
    try {
        const base = process.env.VUE_APP_API_URL;
        if (!base) return;
        const resp = await axios.get(`${base}/auth/map-config`, {timeout: 8000});
        const block = resp && resp.data ? resp.data.data : null;
        if (block) {
            applyBlock(block);
            try {
                localStorage.setItem(STORAGE_KEY, JSON.stringify(block));
            } catch { /* quota - ignore */ }
        }
    } catch { /* keep last-known/bootstrap */ }
};
